/**
 * KinsCircle Upload Reliability Layer V1 — Admin Diagnostics Card
 *
 * Owner / admin-only diagnostics surface mounted on `/admin/health`.
 * Read-only. Aggregates queue + fingerprint state into a small dashboard
 * (uploads in queue, retry count, failed uploads, timeout count, average
 * duration, largest uploads, recent failures).
 *
 * Renders null when:
 *   - the reliability flag is OFF, or
 *   - the viewer is not owner / admin.
 *
 * NEVER mutates uploads. The "Reset queue (admin)" button clears only
 * the in-memory + localStorage queue state — it never deletes any file
 * already uploaded to Supabase Storage.
 *
 * Honors STABILITY.md §1, §3, §7.5, §10.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  CloudUpload,
  Activity,
  AlertTriangle,
  Timer,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import { useAuth } from '@/contexts/AuthContext';
import {
  isUploadReliabilityEnabled,
  canSeeUploadDiagnostics,
} from '@/lib/uploads/featureFlag';
import {
  subscribeQueue,
  resetQueue,
  type UploadQueueItem,
} from '@/lib/uploads/uploadQueue';

const fmtBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtMs = (n: number | null): string => {
  if (n === null || !isFinite(n) || n <= 0) return '—';
  if (n < 1000) return `${n} ms`;
  return `${(n / 1000).toFixed(1)} s`;
};

const Tile: React.FC<{
  label: string;
  value: string | number;
  tone: 'fuchsia' | 'amber' | 'rose' | 'sky' | 'slate' | 'emerald';
  icon: React.ElementType;
}> = ({ label, value, tone, icon: Icon }) => {
  const map = {
    fuchsia: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    sky: 'border-sky-200 bg-sky-50 text-sky-800',
    slate: 'border-slate-200 bg-white text-slate-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  } as const;
  return (
    <div className={`rounded-xl border p-3 ${map[tone]}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-2xl font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
};

const UploadDiagnosticsCard: React.FC = () => {
  const auth = useAuth();
  const role = auth?.role ?? 'member';
  const enabled = isUploadReliabilityEnabled();
  const canSee = canSeeUploadDiagnostics(role);

  const [items, setItems] = useState<UploadQueueItem[]>([]);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    if (!enabled || !canSee) return;
    const unsubscribe = subscribeQueue((snapshot) => setItems(snapshot));
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, canSee, refreshNonce]);

  const stats = useMemo(() => {
    const inQueue = items.filter(
      (i) =>
        i.status === 'queued' ||
        i.status === 'uploading' ||
        i.status === 'retrying' ||
        i.status === 'paused' ||
        i.status === 'restoring',
    ).length;

    const failed = items.filter((i) => i.status === 'failed').length;
    const completed = items.filter((i) => i.status === 'completed').length;
    const timeoutTotal = items.reduce((s, i) => s + (i.timeoutFires || 0), 0);
    const retryTotal = items.reduce(
      (s, i) => s + Math.max(0, (i.attempt || 0) - 1),
      0,
    );

    const completedWithDuration = items.filter(
      (i) => i.status === 'completed' && typeof i.lastDurationMs === 'number',
    );
    const avgDuration =
      completedWithDuration.length > 0
        ? Math.round(
            completedWithDuration.reduce(
              (s, i) => s + (i.lastDurationMs || 0),
              0,
            ) / completedWithDuration.length,
          )
        : null;

    const largest = [...items]
      .sort((a, b) => b.fileSize - a.fileSize)
      .slice(0, 5);

    const recentFailures = items
      .filter((i) => i.status === 'failed')
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, 5);

    return {
      inQueue,
      failed,
      completed,
      timeoutTotal,
      retryTotal,
      avgDuration,
      largest,
      recentFailures,
    };
  }, [items]);

  if (!enabled) return null;
  if (!canSee) return null;

  return (
    <Card className="border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/40 to-pink-50/40">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base flex-wrap">
              <CloudUpload className="w-5 h-5 text-fuchsia-600" />
              Upload Reliability — Diagnostics
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 text-[10px] font-semibold">
                <ShieldCheck className="w-3 h-3" />
                Admin only
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-semibold uppercase">
                V1 · Read-only
              </span>
            </CardTitle>
            <CardDescription className="mt-1">
              Aggregate of the in-browser upload queue. Numbers reflect the
              active session — refresh to re-snapshot. No file in Supabase
              Storage is ever modified by this card.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setRefreshNonce((n) => n + 1)}
              className="rounded-full gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (
                  typeof window !== 'undefined' &&
                  window.confirm(
                    'Reset the in-memory upload queue?\n\nThis clears local queue state ONLY. No uploaded photo or video in Supabase Storage will be deleted or moved.',
                  )
                ) {
                  resetQueue();
                }
              }}
              className="rounded-full gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
            >
              <Trash2 className="w-4 h-4" />
              Reset queue
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top-line tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          <Tile
            label="In queue"
            value={stats.inQueue}
            tone="fuchsia"
            icon={CloudUpload}
          />
          <Tile
            label="Completed"
            value={stats.completed}
            tone="emerald"
            icon={Activity}
          />
          <Tile
            label="Failed"
            value={stats.failed}
            tone="rose"
            icon={AlertTriangle}
          />
          <Tile
            label="Retries"
            value={stats.retryTotal}
            tone="amber"
            icon={RefreshCw}
          />
          <Tile
            label="Timeouts"
            value={stats.timeoutTotal}
            tone="amber"
            icon={Timer}
          />
          <Tile
            label="Avg duration"
            value={fmtMs(stats.avgDuration)}
            tone="sky"
            icon={Timer}
          />
        </div>

        {/* Largest uploads */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
            Largest uploads (this session)
          </div>
          {stats.largest.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No uploads yet.</div>
          ) : (
            <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              {stats.largest.map((it) => (
                <li
                  key={it.id}
                  className="px-3 py-2 flex items-center justify-between gap-2 text-sm"
                >
                  <span className="truncate text-slate-700">{it.fileName}</span>
                  <span className="text-xs tabular-nums text-slate-500 shrink-0">
                    {fmtBytes(it.fileSize)} ·{' '}
                    <span className="font-medium">{it.status}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent failures */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
            Recent failures
          </div>
          {stats.recentFailures.length === 0 ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              No upload failures recorded in this session.
            </div>
          ) : (
            <ul className="rounded-lg border border-rose-200 bg-rose-50/40 divide-y divide-rose-100">
              {stats.recentFailures.map((it) => (
                <li key={it.id} className="px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-800 truncate">
                      {it.fileName}
                    </span>
                    <span className="text-xs text-rose-700 shrink-0">
                      {it.errorCode || 'unknown'}
                    </span>
                  </div>
                  {it.errorMessage && (
                    <div className="text-xs text-rose-700 mt-0.5">
                      {it.errorMessage}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Attempt {it.attempt} / {it.maxAttempts} ·{' '}
                    {new Date(it.updatedAt).toLocaleTimeString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
          V1 ships as an OPT-IN reliability layer. The legacy upload modal
          (photos / videos / memories) continues to use its existing Supabase
          pipeline unchanged. This card reports only on uploads that explicitly
          flow through the new <code>uploadQueue</code> manager.
        </div>
      </CardContent>
    </Card>
  );
};

export default UploadDiagnosticsCard;
